import {
  call,
  put,
  takeLatest,
  takeEvery,
  all,
  fork,
  join,
  select,
} from 'redux-saga/effects';
import { authenticationWrapper } from '@services/errorHandlingService';
import {
  getFromLocalStorage,
  LOCAL_STORAGE_ITEM,
  removeFromLocalStorage,
} from '@utils/localStorageHelper';
import {
  getConversations,
  getFullConversation,
  getUsers,
  createConversation,
  createPost,
  markPostsAsSeen,
  editPost,
  deletePost,
} from './services';
import {
  setConversationsAction,
  setFullConversationAction,
  setUsersAction,
  TYPES,
  getConversationsAction,
  getFullConversationAction,
} from './actions';

import mockConversations from './__fixtures__/conversations.json';
import mockFullConversations from './__fixtures__/fullConversations.json';
import mockUsers from './__fixtures__/users.json';

const useFixtures = process.env.USE_FIXTURES;

export function* callGetConversations() {
  yield authenticationWrapper(function* codeBlock() {
    const res = useFixtures ? mockConversations : yield fork(getConversations);
    const conversations = yield join(res);
    yield put(setConversationsAction(conversations));
  });
}

export function* callGetFullConversation({ id }) {
  yield authenticationWrapper(function* codeBlock() {
    const res = yield fork(getFullConversation, id);
    const currentConversation = useFixtures
      ? mockFullConversations.find((item) => item.id === id)
      : yield join(res);
    yield put(setFullConversationAction(currentConversation));
  });
}

export function* callGetUsers() {
  yield authenticationWrapper(function* codeBlock() {
    const res = yield fork(getUsers);
    const users = useFixtures ? mockUsers : yield join(res);
    yield put(setUsersAction(users));
  });
}

export function* callSendMessage({
  payload: { threadId, messagePayload, participantIds, conversationId },
}) {
  yield authenticationWrapper(function* codeBlock() {
    if (!useFixtures) {
      if (conversationId) {
        const res = yield fork(createPost, threadId, messagePayload);
        const postId = yield join(res);

        yield fork(markPostsAsSeen, postId);
        yield put(getConversationsAction());
        yield put(getFullConversationAction(conversationId));
      } else {
        const response = yield fork(createConversation, participantIds);
        const { newThreadId, newConversationId } = yield join(response);
        const postIdResponse = yield fork(createPost, newThreadId, messagePayload);
        const postId = yield join(postIdResponse);

        yield fork(markPostsAsSeen, postId);
        yield put(getConversationsAction());
        yield put(getFullConversationAction(newConversationId));
      }
    }
  });
}

export function* callMarkPostsAsSeen({ ids }) {
  yield authenticationWrapper(function* codeBlock() {
    if (!useFixtures) {
      yield fork(markPostsAsSeen, ids);
    }
  });
}

export function* callEditMessage({ payload: { postId, payload } }) {
  yield authenticationWrapper(function* codeBlock() {
    if (!useFixtures) {
      const res = yield fork(editPost, postId, payload);
      const editedPostId = yield join(res);
      if (editedPostId) {
        const { currentConversation } = yield select((state) => state.getIn(['messaging']).toJS());
        if (currentConversation?.id) {
          yield put(getFullConversationAction(currentConversation.id));
        }
      }
    }
  });
}

export function* callDeleteMessage({ payload: { postId } }) {
  yield authenticationWrapper(function* codeBlock() {
    if (!useFixtures) {
      const res = yield fork(deletePost, postId);
      const { success } = yield join(res);
      if (success) {
        yield put(getConversationsAction());
        const { currentConversation } = yield select((state) => state.getIn(['messaging']).toJS());
        if (currentConversation?.id) {
          yield put(getFullConversationAction(currentConversation.id));
        }
      }
    }
  });
}

function* watchCallGetConversations() {
  yield takeLatest(TYPES.GET_CONVERSATIONS, callGetConversations);
}
function* watchCallGetFullConversation() {
  yield takeLatest(TYPES.GET_FULL_CONVERSATION, callGetFullConversation);
}
function* watchCallGetUsers() {
  yield takeLatest(TYPES.GET_USERS, callGetUsers);
}
function* watchCallSendMessage() {
  yield takeEvery(TYPES.SEND_MESSAGE, callSendMessage);
}
function* watchCallMarkPostsAsSeen() {
  yield takeEvery(TYPES.MARK_POSTS_AS_SEEN, callMarkPostsAsSeen);
}
function* watchCallEditMessage() {
  yield takeEvery(TYPES.EDIT_MESSAGE, callEditMessage);
}
function* watchCallDeleteMessage() {
  yield takeEvery(TYPES.DELETE_MESSAGE, callDeleteMessage);
}

export default function* messagingSaga() {
  yield all([
    fork(watchCallGetConversations),
    fork(watchCallGetFullConversation),
    fork(watchCallGetUsers),
    fork(watchCallSendMessage),
    fork(watchCallMarkPostsAsSeen),
    fork(watchCallEditMessage),
    fork(watchCallDeleteMessage),
  ]);
}
